// ── Brief Review ──────────────────────────────────────────────
// Renders the compiled Design Brief + Goldfish verdict between the
// interview and the Plan step. The human review gate — Rensin: "show it
// to a real human for approval". See SPEC-GUIDED-INTAKE.md §4.4.
//
// Props:
//   brief     — markdown string (compiled, possibly goldfish-patched)
//   goldfish  — { understood, verdict: 'pass'|'gaps', gaps: [{severity, issue}] }
//   onApprove(editedBrief) — proceed to orchestrate with this exact text
//   onBack    — return to goal step
//   loading   — orchestrate in flight

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function BriefReview({ brief, goldfish, recommendedPatterns = [], onApprove, onBack, loading }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(brief);

  const pass = goldfish?.verdict === 'pass';
  const gaps = goldfish?.gaps || [];

  return (
    <div className="wfs-brief">
      <div className="wfs-brief-header">
        <h2 className="wfs-brief-title">Your Design Brief</h2>
        <p className="wfs-brief-sub">
          This document is what the workflow designer will build from. Edit anything that's off.
        </p>
      </div>

      {/* Goldfish verdict banner */}
      <div
        className={`wfs-brief-goldfish ${pass ? 'wfs-brief-goldfish--pass' : 'wfs-brief-goldfish--gaps'}`}
        role="status"
        title="A fresh AI reviewer read this brief with zero context from your interview — if it can execute from the document alone, the document is solid."
      >
        <span className="wfs-brief-goldfish-icon">🐟</span>
        <div className="wfs-brief-goldfish-body">
          <strong>
            {pass
              ? 'Goldfish review: a context-free reviewer could execute this brief.'
              : 'Goldfish review found gaps:'}
          </strong>
          {goldfish?.understood && (
            <div className="wfs-brief-goldfish-understood">"{goldfish.understood}"</div>
          )}
          {!pass && gaps.length > 0 && (
            <ul className="wfs-brief-goldfish-gaps">
              {gaps.map((g, i) => (
                <li key={i} className={`wfs-brief-gap wfs-brief-gap--${g.severity}`}>
                  <span className="wfs-brief-gap-sev">{g.severity}</span> {g.issue}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Answer-driven thinking patterns the workflow designer will weave in */}
      {recommendedPatterns.length > 0 && (
        <div className="wfs-brief-patterns" role="note">
          <div className="wfs-brief-patterns-label">
            🧩 Thinking patterns selected from your answers:
          </div>
          <div className="wfs-brief-patterns-list">
            {recommendedPatterns.map(p => (
              <div className="wfs-brief-pattern" key={p.id} title={p.reason}>
                <span className="wfs-brief-pattern-name">{p.name || p.id}</span>
                {p.reason && <span className="wfs-brief-pattern-reason">{p.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brief body — rendered or editable */}
      <div className="wfs-brief-doc">
        <div className="wfs-brief-doc-toolbar">
          <button
            type="button"
            className="wfs-btn wfs-btn--ghost wfs-brief-edit-btn"
            onClick={() => setEditing(e => !e)}
            disabled={loading}
          >
            {editing ? '👁 Preview' : '✏️ Edit'}
          </button>
        </div>
        {editing ? (
          <textarea
            className="wfs-brief-textarea"
            value={text}
            onChange={e => setText(e.target.value)}
            disabled={loading}
            spellCheck={false}
          />
        ) : (
          <div className="wfs-brief-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
          </div>
        )}
      </div>

      <div className="wfs-brief-actions">
        <button type="button" className="wfs-btn wfs-btn--ghost" onClick={onBack} disabled={loading}>
          ← Back
        </button>
        <button
          type="button"
          className="wfs-btn wfs-btn--primary"
          onClick={() => onApprove(text)}
          disabled={loading || !text.trim()}
        >
          {loading ? 'Designing…' : 'Looks right — design the workflow →'}
        </button>
      </div>
    </div>
  );
}
